import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../AppIcon';
import Button from './Button';

/**
 * A file input that behaves the way its own copy claims.
 *
 * The upload blocks in this app were copy-pasted per form — roughly eighty
 * lines each of drop zone, type check, size check, preview and remove. Three
 * problems came with the duplication:
 *
 *   1. **Most drop zones didn't accept drops.** The author portal's uploader
 *      implements drag and drop properly; the setup wizard's two copies said
 *      "drag and drop your file here" with no drag handler at all, so the
 *      invitation was decorative. Behaviour depended on which form you were in.
 *   2. **Object URLs leaked.** `src={URL.createObjectURL(file)}` was evaluated
 *      inline in render, so a new blob URL was minted on every keystroke
 *      elsewhere in the form and none were ever revoked.
 *   3. **Rejections were silent or shouted.** Type and size failures went to a
 *      toast, away from the field that caused them, so the error and the control
 *      were never on screen together.
 *
 * This does drag and drop for real, revokes the preview URL when the file
 * changes or the component unmounts, and puts the rejection message under the
 * field where it happened.
 */
const FileField = ({
  label,
  hint,
  file,
  onChange,
  accept,
  maxSizeMb = 10,
  preview = false,
  icon = 'Upload',
  error,
  id,
}) => {
  const { t } = useTranslation('common');
  const generatedId = useId();
  const inputId = id || `file-${generatedId}`;
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [localError, setLocalError] = useState('');
  const [previewUrl, setPreviewUrl] = useState(null);

  // One object URL per file, revoked when it's replaced or the field unmounts.
  useEffect(() => {
    if (!preview || !file) {
      setPreviewUrl(null);
      return undefined;
    }
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file, preview]);

  const acceptList = (accept || '')
    .split(',')
    .map((s) => s.trim().toLowerCase())
    .filter(Boolean);

  // "image/jpeg,image/png,image/webp" is a machine's answer to "what can I
  // upload?". People want "JPG, PNG, WEBP".
  const acceptLabel = Array.from(
    new Set(
      acceptList.map((a) => {
        const raw = a.startsWith('.') ? a.slice(1) : a.split('/').pop();
        return (raw === 'jpeg' ? 'jpg' : raw).toUpperCase();
      })
    )
  ).join(', ');

  const isAccepted = (f) => {
    if (acceptList.length === 0) return true;
    const type = (f?.type || '').toLowerCase();
    const name = (f?.name || '').toLowerCase();
    return acceptList.some((a) =>
      a.startsWith('.') ? name.endsWith(a) : a.endsWith('/*') ? type.startsWith(a.slice(0, -1)) : type === a
    );
  };

  const take = useCallback(
    (f) => {
      if (!f) return;
      if (!isAccepted(f)) {
        setLocalError(t('file.wrongType', { accept: acceptLabel, defaultValue: 'That file type isn’t accepted. Allowed: {{accept}}' }));
        return;
      }
      if (f.size > maxSizeMb * 1024 * 1024) {
        setLocalError(
          t('file.tooLarge', {
            size: (f.size / 1024 / 1024).toFixed(1),
            max: maxSizeMb,
            defaultValue: 'That file is {{size}} MB. The limit is {{max}} MB.',
          })
        );
        return;
      }
      setLocalError('');
      onChange?.(f);
    },
    [acceptList, maxSizeMb, onChange, t]
  );

  const shownError = error || localError;

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    take(e.dataTransfer?.files?.[0]);
  };

  return (
    <div className="space-y-1.5">
      {label && (
        <label htmlFor={inputId} className="text-xs font-medium text-foreground block">
          {label}
        </label>
      )}
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}

      {file ? (
        <div className="p-3 bg-muted/40 rounded-lg border border-border">
          {preview && previewUrl && (
            <img
              src={previewUrl}
              alt=""
              className="w-full h-36 object-cover rounded-md mb-3"
            />
          )}
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2.5 min-w-0">
              <div className="w-8 h-8 rounded-md bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Icon name={preview ? 'Image' : 'FileText'} size={15} className="text-primary" />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-medium text-foreground truncate">{file.name}</p>
                <p className="text-xs text-muted-foreground tabular-nums">
                  {(file.size / 1024 / 1024).toFixed(2)} MB
                </p>
              </div>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => { setLocalError(''); onChange?.(null); }}
              iconName="X"
              aria-label={t('file.remove', 'Remove file')}
            />
          </div>
        </div>
      ) : (
        <div
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
          className={`border border-dashed rounded-lg p-5 text-center transition-colors ${
            dragging ? 'border-primary bg-primary/5' : shownError ? 'border-error' : 'border-border'
          }`}
        >
          <div className="w-9 h-9 mx-auto mb-2 rounded-lg bg-muted flex items-center justify-center">
            <Icon name={icon} size={16} className="text-muted-foreground" />
          </div>
          <p className="text-sm text-foreground">
            {t('file.dropHere', 'Drag a file here')}
          </p>
          {accept && (
            <p className="text-xs text-muted-foreground mt-0.5">
              {t('file.limits', { accept: acceptLabel, max: maxSizeMb, defaultValue: '{{accept}} · up to {{max}} MB' })}
            </p>
          )}
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="mt-3"
            onClick={() => inputRef.current?.click()}
            iconName="Upload"
            iconPosition="left"
          >
            {t('file.choose', 'Choose a file')}
          </Button>
        </div>
      )}

      <input
        ref={inputRef}
        id={inputId}
        type="file"
        accept={accept}
        className="sr-only"
        onChange={(e) => {
          take(e.target.files?.[0]);
          // Reset so picking the same file twice still fires a change.
          e.target.value = '';
        }}
      />

      {shownError && (
        <p className="text-xs text-error flex items-center gap-1" role="alert">
          <Icon name="AlertCircle" size={12} />
          {shownError}
        </p>
      )}
    </div>
  );
};

export default FileField;
